/**
 * Superhost Install Prompt — PWA "Add to Home Screen" banner.
 *
 * UX:
 *   • Captures beforeinstallprompt event (Chrome/Edge desktop + Android)
 *   • Shows a subtle bottom banner with gold Install button
 *   • Dismiss hides for 7 days (localStorage flag)
 *   • Hidden automatically when app is already installed (display-mode: standalone)
 */
import React, { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';

const DISMISS_KEY = 'sh_install_dismissed_at';
const DISMISS_DAYS = 7;

const isStandalone = () =>
  (typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true));

const recentlyDismissed = () => {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY));
    if (!ts) return false;
    const diffMs = Date.now() - ts;
    return diffMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {}
    setDeferredPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-20 left-3 right-3 z-40 bg-[#1B2A7B] rounded-2xl shadow-premium-lg border border-white/20 p-4 flex items-center gap-3 animate-fadeIn"
      data-testid="install-prompt"
      role="dialog"
      aria-label="Install Superhost"
    >
      <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center flex-shrink-0 overflow-hidden">
        <img src="/icons/icon-96.png" alt="Superhost" className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-semibold leading-tight">Add Superhost to Home Screen</p>
        <p className="text-white/80 text-[11px] mt-0.5">One-tap launch, works offline</p>
      </div>
      <button
        onClick={install}
        className="px-4 py-2 rounded-full bg-white text-[#1B2A7B] text-xs font-semibold shadow-premium hover:bg-[#EEF1FC] transition-colors flex items-center gap-1.5"
        data-testid="install-prompt-cta"
      >
        <Download className="w-3.5 h-3.5" />
        Install
      </button>
      <button
        onClick={dismiss}
        className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Dismiss"
        data-testid="install-prompt-dismiss"
      >
        <X className="w-4 h-4 text-white/70" />
      </button>
    </div>
  );
};

export default InstallPrompt;
